import {Flex} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {CodonTranslationTable, GENETIC_CODE_USER_PREFERENECE_KEY, NucleotideType} from 'biojs/BioConstants';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {CustomField, Strand} from 'constants/MolbioConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {TRANSLATION_MAX_NOTES_LENGTH} from 'constants/SequenceConstants';
import {
  EditTranslationRegionGroupData,
  TranslationRegionModification,
  TranslationRegionModificationType,
} from 'molbio/dao/SequenceDAO';
import CustomFieldsForm from 'molbio/sequence-features/CustomFieldsForm';
import PopoverColorPicker from 'molbio/sequence-features/PopoverColorPicker';
import StrandDropdown from 'molbio/sequence-features/StrandDropdown';
import GeneticCodeDropdown from 'molbio/translations/GeneticCodeDropdown';
import {
  countAminoAcids,
  DEFAULT_TRANSLATION_COLOR,
  translateRegions,
  TranslationRegionGroupData,
  TRANSLATION_EDIT_FIELDS,
  validateTranslationForm,
  confirmTranslationLinkInvalidationRisk,
} from 'molbio/translations/TranslationUtil';
import notifications from 'notifications';
import {FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {useFeaturePanelStateBySequence} from 'toolbar/feature-panel/reduceFeaturePanelUI';
import {CreateNewTranslationData} from 'toolbar/translation-panel/TranslationPanelUtil';
import TranslationPositionInputGroup from 'toolbar/translation-panel/TranslationPositionInputGroup';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, GRAY_LIGHT} from 'ui-library/Colors';
import usePreference from 'ui-library/hooks/usePreference';
import AddIcon from 'ui-library/icons/AddIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextInput from 'ui-library/inputs/TextInput';
import {immutableRemoveAt, immutableReplace} from 'util/ImmutableListUtil';
import {isEnterKey} from 'util/Keyboard';

export interface TranslationRegion {
  start: number | null;
  end: number | null;
  id?: number;
  selected?: boolean;
}

interface TranslationFormData {
  name: string;
  strand: Strand;
  notes: string;
  customFields: Array<CustomField>;
  codonTranslationTable: CodonTranslationTable;
  coerceStartCodonToMethionine: boolean;
  color: string;
}

function getFormDataFromTranslation(
  group: TranslationRegionGroupData | null,
  preferredGeneticCode: CodonTranslationTable
): TranslationFormData {
  return {
    name: group?.name ?? '',
    strand: group?.strand ?? 1,
    notes: group?.notes ?? '',
    customFields: group?.customFields ?? [],
    codonTranslationTable: group?.codonTranslationTable ?? preferredGeneticCode,
    color: group?.color ?? DEFAULT_TRANSLATION_COLOR,
    coerceStartCodonToMethionine: group?.coerceStartCodonToMethionine ?? false,
  };
}

function getAllUniqueCustomFieldKeys(allTranslationData: Array<TranslationRegionGroupData>): Array<string> {
  const uniqueKeys = new Set(
    allTranslationData.flatMap(({customFields}) => customFields.map(({name}) => name))
  );
  return [...uniqueKeys];
}

export interface CreateEditTranslationFormProps {
  // Data for all existing translations on the sequence.
  allTranslationData: Array<TranslationRegionGroupData>;
  // Data for the translation currently being edited. Only required if the form is used for editing.
  editedTranslationData: TranslationRegionGroupData | null;
  sequenceId: string;
  sequenceStartIndex: number;
  sequenceLength: number;
  sequenceBases: string;
  nucleotideType: NucleotideType;
  isSingleStranded: boolean;
  isCircularSequence: boolean;
  onClickCancel: () => void;
  deleteTranslationRegionGroup: (groupCid: string) => Promise<void>;
  editTranslationRegionGroup: (groupCid: string, data: EditTranslationRegionGroupData) => void;
  createNewTranslationRegionGroup: (data: CreateNewTranslationData) => void;
  isReadOnly: boolean;
  tagSchema?: TagSchema;
}

/**
 * The form used in the translations tab of the feature panel in the molbio toolbar
 * to edit existing translations or create new translations.
 */
function CreateEditTranslationForm({
  allTranslationData,
  editedTranslationData,
  sequenceId,
  sequenceStartIndex,
  sequenceLength,
  sequenceBases,
  nucleotideType,
  isSingleStranded,
  isCircularSequence,
  onClickCancel,
  deleteTranslationRegionGroup,
  editTranslationRegionGroup,
  createNewTranslationRegionGroup,
  isReadOnly,
  tagSchema,
}: CreateEditTranslationFormProps): JSX.Element {
  const [geneticCodeUserPreference, setGeneticCodeUserPreference] = usePreference<CodonTranslationTable>(
    GENETIC_CODE_USER_PREFERENECE_KEY
  );
  const [translationFormData, setTranslationFormData] = useState<TranslationFormData>(
    getFormDataFromTranslation(editedTranslationData, geneticCodeUserPreference)
  );
  const [existingTranslationRegionGroup, setExistingTranslationRegionGroup] =
    useState<TranslationRegionGroupData | null>(editedTranslationData);
  const [newRegions, setNewRegions] = useState<Array<TranslationRegion>>([]);
  const [removedExistingRegionIds, setRemovedExistingRegionIds] = useState<Set<number>>(new Set());
  const {translationPanelState, selectionStart, selectionEnd} = useFeaturePanelStateBySequence(sequenceId);
  const {isCreateActive, initialStrand} = translationPanelState;

  const disableSave =
    isReadOnly ||
    (isCreateActive && newRegions.length === 0) ||
    newRegions.some((r) => r.start == null || r.end == null);

  useEffect(() => {
    if (isCreateActive) {
      setNewRegions([{start: selectionStart, end: selectionEnd}]);
    } else {
      setNewRegions([]);
    }
    // Only initialize on create mode change, selection syncing is handled by the separate effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateActive]);

  // Sync selection only when there's exactly 1 new region and in create mode
  useEffect(() => {
    if (isCreateActive && newRegions.length === 1) {
      setNewRegions([{start: selectionStart, end: selectionEnd}]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Syncing should only happen when the selection changes
  }, [selectionStart, selectionEnd]);

  const orderedExistingRegions: Array<TranslationRegion> = useMemo(() => {
    const regions =
      existingTranslationRegionGroup?.translations
        .filter((t) => !removedExistingRegionIds.has(t.id))
        .map((t) => ({
          start: t.start,
          end: t.end,
          id: t.id,
        })) ?? [];
    if (translationFormData.strand === -1) {
      return [...regions].reverse();
    }
    return regions;
  }, [existingTranslationRegionGroup, removedExistingRegionIds, translationFormData.strand]);

  const allRegions: Array<TranslationRegion> = useMemo(() => {
    const existingRegions =
      orderedExistingRegions.map((t) => ({
        start: t.start,
        end: t.end,
        id: t.id,
      })) ?? [];
    return [...existingRegions, ...newRegions.map((r) => ({start: r.start, end: r.end}))];
  }, [orderedExistingRegions, newRegions]);

  useEffect(() => {
    if (!isCreateActive) {
      setExistingTranslationRegionGroup(editedTranslationData);
    }
  }, [editedTranslationData, isCreateActive]);

  useEffect(() => {
    if (isCreateActive) {
      setTranslationFormData((currentData) => ({...currentData, strand: initialStrand ?? 1}));
      setExistingTranslationRegionGroup(null);
    }
  }, [isCreateActive, initialStrand]);

  function updateFormData(data: Partial<TranslationFormData>): void {
    setTranslationFormData({...translationFormData, ...data});
  }

  function addNewRegion(): void {
    if (isCreateActive && newRegions.length === 0) {
      setNewRegions([{start: selectionStart, end: selectionEnd}]);
    } else {
      setNewRegions([...newRegions, {start: null, end: null}]);
    }
  }

  function removeNewRegion(index: number): void {
    setNewRegions(immutableRemoveAt(newRegions, index));
  }

  function updateNewRegionStart(index: number, value: number | null): void {
    setNewRegions(immutableReplace(newRegions, index, {...newRegions[index], start: value}));
  }

  function updateNewRegionEnd(index: number, value: number | null): void {
    setNewRegions(immutableReplace(newRegions, index, {...newRegions[index], end: value}));
  }

  function removeExistingRegion(regionId: number): void {
    setRemovedExistingRegionIds(new Set([...removedExistingRegionIds, regionId]));
  }

  function getCompleteSortedRegions(): Array<TranslationRegion> {
    return allRegions
      .filter(({start, end}) => start != null && end != null)
      .sort((a, b) => a.start! - b.start!);
  }

  // Gets the start and end range of all regions in the currently edited translation group
  // including new regions being added
  function translationGroupRange(): {start: number | null; end: number | null} {
    const completeRegions = getCompleteSortedRegions();
    if (completeRegions.length === 0) {
      return {start: null, end: null};
    }
    return {
      start: completeRegions[0].start,
      end: completeRegions[completeRegions.length - 1].end,
    };
  }

  // Gets the region modification spec for the given translation ID according to the current form state
  // For a deleted region, return a DELETE spec
  // For a non-deleted region, add an UPDATE spec if the strand has changed
  function getModificationSpec(id: number): TranslationRegionModification | null {
    if (removedExistingRegionIds.has(id)) {
      return {
        modificationType: TranslationRegionModificationType.DELETE,
        id,
      };
    } else if (translationFormData.strand !== existingTranslationRegionGroup!.strand) {
      return {
        modificationType: TranslationRegionModificationType.UPDATE,
        id,
        strand: translationFormData.strand,
      };
    } else {
      return null;
    }
  }

  // Converts new regions to the format expected for create/edit operations
  function getNewRegionSpecs(): Array<{start: number; end: number; strand: Strand}> {
    return newRegions
      .filter((r) => r.start != null && r.end != null)
      .map((r) => ({
        start: r.start!,
        end: r.end === sequenceLength ? 0 : r.end!,
        strand: translationFormData.strand,
      }));
  }

  function trackOnClickSave(action: string, editedFields?: Array<string>): void {
    let translationIdMetadata: {[key: string]: number | null} = {
      translation_group_id: null,
      translation_id: null,
    };
    if (action !== 'create_new') {
      translationIdMetadata = {
        translation_group_id: existingTranslationRegionGroup!.hasGroup
          ? parseInt(existingTranslationRegionGroup!.cid, 10)
          : null,
        translation_id: existingTranslationRegionGroup!.hasGroup
          ? null
          : existingTranslationRegionGroup?.translations[0].id!,
      };
    }

    analytics.track('translation', 'on_click_save', {
      product: ANALYTICS_MODULE.MOLBIO,
      sequence_id: sequenceId,
      action,
      edited_fields: editedFields ?? [],
      ...translationIdMetadata,
    });
  }

  async function onClickSave(): Promise<void> {
    const groupProperties = {
      name: translationFormData.name,
      notes: translationFormData.notes,
      customFields: translationFormData.customFields,
      codonTranslationTable: translationFormData.codonTranslationTable,
      color: translationFormData.color,
      coerceStartCodonToMethionine: translationFormData.coerceStartCodonToMethionine,
    };

    // If a translation is not being fully deleted, validate the changes before saving
    if (allRegions.length > 0) {
      const regionsForValidation = allRegions.map((r) => ({
        start: r.start,
        end: r.end === sequenceLength ? 0 : r.end,
      }));
      const errorMessage = validateTranslationForm(
        {
          name: translationFormData.name,
          isCreateActive: newRegions.length > 0,
          regions: regionsForValidation,
          customFields: translationFormData.customFields,
        },
        sequenceStartIndex,
        sequenceLength,
        isCircularSequence
      );
      if (errorMessage != null) {
        notifications.error(errorMessage);
        return;
      }
    }

    if (isCreateActive) {
      // Create mode: all regions are new
      createNewTranslationRegionGroup({
        ...groupProperties,
        strand: translationFormData.strand,
        regions: getNewRegionSpecs(),
      });
      trackOnClickSave('create_new');
      setGeneticCodeUserPreference(groupProperties.codonTranslationTable);
    } else if (allRegions.length === 0) {
      // If all regions were removed from the translation, directly delete it
      await deleteTranslationRegionGroup(existingTranslationRegionGroup!.cid);
      trackOnClickSave('delete');
    } else {
      // Edit mode: combine delete/update specs for existing + create specs for new
      const editedFields = TRANSLATION_EDIT_FIELDS.filter(
        (field) => translationFormData[field] !== existingTranslationRegionGroup![field]
      );
      if (
        !(await confirmTranslationLinkInvalidationRisk(
          tagSchema,
          editedFields.includes('codonTranslationTable'),
          editedFields.includes('coerceStartCodonToMethionine') &&
            Boolean(editedTranslationData?.coerceStartCodonToMethionine),
          false,
          1
        ))
      ) {
        return;
      }

      const modifications: Array<TranslationRegionModification> = [
        // DELETE/UPDATE specs for existing regions
        ...existingTranslationRegionGroup!.translations
          .map(({id}) => getModificationSpec(id))
          .filter((spec): spec is TranslationRegionModification => spec != null),
        // CREATE specs for new regions
        ...getNewRegionSpecs().map((r) => ({
          ...r,
          modificationType: TranslationRegionModificationType.CREATE as const,
        })),
      ];

      editTranslationRegionGroup(existingTranslationRegionGroup!.cid, {
        regions: modifications,
        properties: groupProperties,
      });
      trackOnClickSave('edit', editedFields);
      if (editedFields.includes('codonTranslationTable')) {
        setGeneticCodeUserPreference(groupProperties.codonTranslationTable);
      }
    }
  }

  return (
    <div
      className={css(styles.formContainer)}
      onKeyDown={(e) => {
        if (!disableSave && isEnterKey(e)) {
          onClickSave();
        }
      }}
      {...getTestProps({component: 'CreateEditTranslationForm', element: 'root'})}
    >
      {isCreateActive && (
        <FieldLabel className={css(styles.newTranslationTitle)} label="Create translation" />
      )}

      <FormGroup className={css(sharedFormStyles.fieldRow)}>
        <FieldLabel className={css(sharedFormStyles.inputLabel)} label="Name" />
        <TextInput
          className={css(sharedFormStyles.fieldInput)}
          placeholder="Translation name"
          value={translationFormData.name}
          onChange={(e) => {
            updateFormData({name: e.target.value});
          }}
          disabled={isReadOnly}
          dataTestAttrs={{component: 'CreateEditTranslationForm', element: 'nameInput'}}
        />
      </FormGroup>

      <TranslationPositionInputGroup
        label="Position"
        modelRange={translationGroupRange()}
        setStart={null}
        setEnd={null}
        disabled={true}
        sequenceStartIndex={sequenceStartIndex}
        sequenceLength={sequenceLength}
      />
      <FormGroup className={css(sharedFormStyles.fieldRow)}>
        <FieldLabel className={css(sharedFormStyles.inputLabel)} label="# of AA's" />
        <TextInput
          className={css(sharedFormStyles.fieldInput)}
          value={countAminoAcids(
            translateRegions(
              getCompleteSortedRegions(),
              sequenceBases,
              translationFormData.strand,
              nucleotideType,
              translationFormData.codonTranslationTable,
              translationFormData.coerceStartCodonToMethionine
            )
          )}
          disabled={true}
          dataTestAttrs={{component: 'CreateEditTranslationForm', element: 'aaLengthInput'}}
        />
      </FormGroup>

      <FormGroup className={css(sharedFormStyles.fieldRow)}>
        <FieldLabel className={css(sharedFormStyles.inputLabel)} label="Genetic code" />
        <GeneticCodeDropdown
          className={css(sharedFormStyles.fieldInput)}
          geneticCode={translationFormData.codonTranslationTable}
          onSelectGeneticCode={(geneticCode: CodonTranslationTable) => {
            updateFormData({codonTranslationTable: geneticCode});
            analytics.track('translation', 'changed_genetic_code', {
              product: ANALYTICS_MODULE.MOLBIO,
              sequence_id: sequenceId,
            });
          }}
          disabled={isReadOnly}
        />
      </FormGroup>
      <FormGroup className={css(sharedFormStyles.fieldRow)}>
        <FieldLabel className={css(sharedFormStyles.inputLabel)} label="First codon" />
        <CheckboxInput
          className={css(sharedFormStyles.fieldInput)}
          onChange={(e) => {
            updateFormData({coerceStartCodonToMethionine: e.target.checked});
          }}
          checked={translationFormData.coerceStartCodonToMethionine}
          id="methionine-checkbox"
          label="Translate as methionine"
          disabled={isReadOnly}
          dataTestAttrs={{component: 'CreateEditTranslationForm', element: 'coerceStartCodonCheckbox'}}
        />
      </FormGroup>
      <FormGroup className={css(sharedFormStyles.fieldRow)}>
        <FieldLabel className={css(sharedFormStyles.inputLabel)} label="Color" />
        <PopoverColorPicker
          className={css(sharedFormStyles.fieldInput)}
          selectedColor={translationFormData.color}
          onSelectColor={(color) => {
            updateFormData({color});
          }}
          asDropdown={true}
          disabled={isReadOnly}
        />
      </FormGroup>

      <FormGroup className={css(sharedFormStyles.fieldRow)}>
        <FieldLabel className={css(sharedFormStyles.inputLabel)} label="Strand" />
        <StrandDropdown
          className={css(sharedFormStyles.fieldInput)}
          strand={translationFormData.strand}
          onSelectStrand={(value: Strand) => {
            updateFormData({strand: value});
          }}
          disabled={isSingleStranded || isReadOnly}
          allowNone={false}
        />
      </FormGroup>
      <FormGroup className={css(sharedFormStyles.fieldRow)}>
        <FieldLabel className={css(styles.notesLabel)} label="Notes" />
        <textarea
          className={css(sharedFormStyles.fieldInput)}
          placeholder="Notes"
          maxLength={TRANSLATION_MAX_NOTES_LENGTH}
          value={translationFormData.notes}
          onChange={(e) => {
            updateFormData({notes: e.target.value});
          }}
          rows={translationFormData.notes ? 3 : 1}
          disabled={isReadOnly}
        />
      </FormGroup>

      <CustomFieldsForm
        className={css(styles.customFields)}
        customFields={translationFormData.customFields}
        setCustomFields={(newFields) => {
          updateFormData({customFields: newFields});
        }}
        allCustomFieldKeys={getAllUniqueCustomFieldKeys(allTranslationData)}
        emptyFieldsMessage="This translation has no custom fields"
        isReadOnly={isReadOnly}
      />

      <Flex
        gap="xs"
        m="0 12px 12px"
        pb="8px"
        sx={(theme) => ({borderBottom: `1px solid ${theme.colors.gray[2]}`})}
      >
        <FieldLabel className={css(styles.regionsSectionLabel)} label="Regions" />
        {!isReadOnly && (
          <AddIcon
            className={css(styles.addRegionIcon)}
            onClick={addNewRegion}
            altText="Add region"
            dataTestAttrs={{component: 'CreateEditTranslationForm', element: 'addRegionButton'}}
          />
        )}
      </Flex>
      {orderedExistingRegions.map((t, idx) => (
        <TranslationPositionInputGroup
          key={`existing-${t.id ?? idx}`}
          regionLabelClassName={css(styles.regionsLabel)}
          label={`Region ${idx + 1}`}
          modelRange={{start: t.start, end: t.end, id: t.id}}
          setStart={null}
          setEnd={null}
          disabled={true}
          sequenceStartIndex={sequenceStartIndex}
          sequenceLength={sequenceLength}
          onRemove={
            isReadOnly
              ? null
              : () => {
                  // Existing regions will always have an ID
                  removeExistingRegion(t.id!);
                }
          }
        />
      ))}
      {newRegions.map((region, idx) => (
        <TranslationPositionInputGroup
          key={`new-${idx}`}
          regionLabelClassName={css(styles.regionsLabel)}
          label="New region"
          modelRange={region}
          setStart={(v) => {
            updateNewRegionStart(idx, v);
          }}
          setEnd={(v) => {
            updateNewRegionEnd(idx, v);
          }}
          disabled={false}
          sequenceStartIndex={sequenceStartIndex}
          sequenceLength={sequenceLength}
          onRemove={() => {
            removeNewRegion(idx);
          }}
        />
      ))}

      <div className={css(styles.buttonRow)}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.SMALL}
          onClick={onClickCancel}
          text="Cancel"
        />
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.SMALL}
          onClick={onClickSave}
          text="Save"
          disabled={disableSave}
          dataTestAttrs={{component: 'CreateEditTranslationForm', element: 'saveButton'}}
        />
      </div>
    </div>
  );
}

export const sharedFormStyles = StyleSheet.create({
  fieldRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 12px',
  },
  inputLabel: {
    margin: 'auto 0',
  },
  fieldInput: {
    width: '172px',
  },
});

const styles = StyleSheet.create({
  newTranslationTitle: {
    padding: '0 12px',
    marginBottom: '16px',
  },
  formContainer: {
    padding: '12px 0',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: `1px solid ${GRAY_LIGHT}`,
    padding: '16px 10px 0',
  },
  regionsSectionLabel: {
    color: GRAY_DARKER,
    marginBottom: 0,
  },
  addRegionIcon: {
    margin: 'auto 0',
    ':focus': {
      outline: 'none',
    },
  },
  regionsLabel: {
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    margin: 'auto 0',
  },
  customFields: {
    margin: '16px 12px',
  },
  notesLabel: {
    marginTop: 6,
  },
});

export default CreateEditTranslationForm;
